import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { getEmpresaId } from '../services/sessionService';

export default function NuevoInstrumento() {
  const navigate = useNavigate();
  const [formulario, setFormulario] = useState({});
  const [campos, setCampos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [mensajeError, setMensajeError] = useState('');

  const camposObligatorios = ['nombre', 'identificacion', 'fecha_ultima', 'fecha_proxima'];
  const etiquetasCampos = {
    nombre: 'Nombre',
    identificacion: 'Identificación',
    rango_medicion: 'Rango de medición',
    marca: 'Marca',
    modelo: 'Modelo',
    numero_serie: 'Número de serie',
    ubicacion: 'Ubicación',
    estado_calibracion: 'Estado de calibración',
    proveedor: 'Proveedor',
    notas: 'Notas',
    fecha_ultima: 'Fecha última',
    fecha_proxima: 'Fecha próxima',
    periodicidad_meses: 'Periodicidad (meses)',
    tolerancia: 'Tolerancia',
    precio: 'Precio',
    tipo: 'Tipo',
    condicion: 'Condición',
    certificado: 'Certificado',
    carta: 'Carta',
    asignado: 'Asignado a',
  };

  const obtenerEtiqueta = (campo) =>
    etiquetasCampos[campo] ||
    campo
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (letra) => letra.toUpperCase());

  useEffect(() => {
    let cancelado = false;

    const obtenerCampos = async () => {
      setLoading(true);
      setErrorCarga('');

      try {
        const { data: columnasData, error: errorColumnas } = await supabase
          .rpc('get_column_names', {
            table_name_input: 'instrumentos',
          })
          .abortSignal(AbortSignal.timeout(8000));

        if (errorColumnas) {
          throw new Error(`Columnas: ${errorColumnas.message}`);
        }

        const columnas = (columnasData || []).map((c) => c.column_name);

        const columnasExcluidas = [
          'id',
          'empresa_id',
          'activo',
          'created_at',
          'updated_at',
          'creado_en',
          'creadoEn',
        ];

        const camposFiltrados = columnas.filter(
          (c) => !columnasExcluidas.includes(c)
        );

        const objetoInicial = {};

        camposFiltrados.forEach((c) => {
          objetoInicial[c] = '';
        });

        if (!cancelado) {
          setCampos(camposFiltrados);
          setFormulario(objetoInicial);
        }
      } catch (error) {
        console.error('❌ Error cargando formulario de instrumento:', error);

        if (!cancelado) {
          setErrorCarga(
            'No se pudo preparar el formulario. Verifica tu conexión e inténtalo nuevamente.'
          );
        }
      } finally {
        if (!cancelado) {
          setLoading(false);
        }
      }
    };

    obtenerCampos();

    return () => {
      cancelado = true;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormulario(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (guardando) return;

    setMensaje('');
    setMensajeError('');

    const camposFaltantes = camposObligatorios.filter(
      (c) => !formulario[c]?.trim()
    );

    if (camposFaltantes.length > 0) {
      setMensajeError(
        'Completa los campos obligatorios: ' + camposFaltantes.join(', ')
      );
      return;
    }

    const empresa_id = getEmpresaId();

    if (!empresa_id) {
      setMensajeError(
        'No se pudo identificar la empresa del usuario. Vuelve a iniciar sesión e inténtalo nuevamente.'
      );
      return;
    }

    const instrumento = {
      ...formulario,
      activo: true,
      empresa_id,
      tolerancia:
        formulario.tolerancia === ''
          ? null
          : parseFloat(formulario.tolerancia),
      precio:
        formulario.precio === ''
          ? null
          : parseFloat(formulario.precio),
      periodicidad_meses:
        formulario.periodicidad_meses === ''
          ? null
          : parseInt(formulario.periodicidad_meses, 10),
    };

    setGuardando(true);

    try {
      const { data: existentes, error: errorVerificacion } = await supabase
        .from('instrumentos')
        .select('id')
        .eq('identificacion', instrumento.identificacion)
        .eq('empresa_id', empresa_id)
        .abortSignal(AbortSignal.timeout(8000));

      if (errorVerificacion) {
        throw new Error(
          `Verificación de duplicados: ${errorVerificacion.message}`
        );
      }

      if ((existentes || []).length > 0) {
        setMensajeError(
          `Ya existe un instrumento con la identificación "${instrumento.identificacion}".`
        );
        setGuardando(false);
        return;
      }

      const { error } = await supabase
        .from('instrumentos')
        .insert([instrumento])
        .abortSignal(AbortSignal.timeout(8000));

      if (error) {
        throw new Error(`Guardado: ${error.message}`);
      }

      setMensaje('Instrumento guardado correctamente.');

      setTimeout(() => {
        navigate('/instrumentos');
      }, 800);
    } catch (error) {
      console.error('❌ Error al guardar instrumento:', error);

      setMensajeError(
        'No se pudo guardar el instrumento. Verifica tu conexión e inténtalo nuevamente.'
      );

      setGuardando(false);
    }
  };
  if (loading) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-sm text-slate-500">Preparando formulario...</p>
        </div>
      </div>
    );
  }

  if (errorCarga) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="max-w-3xl rounded-2xl border border-red-200 bg-white p-6 shadow-sm">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {errorCarga}
          </div>

          <button
            onClick={() => navigate('/instrumentos')}
            className="mt-4 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Volver a Instrumentos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-600">
          Instrumentos
        </p>

        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          Agregar instrumento
        </h1>

        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
          Registra la información necesaria para incorporar un nuevo instrumento
          al programa de calibración.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      >
        <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
          <h2 className="font-semibold text-slate-900">
            Información del instrumento
          </h2>

          <p className="mt-1 text-xs text-slate-500">
            Los campos marcados con * son obligatorios.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-x-6 gap-y-5 p-5 sm:p-6 md:grid-cols-2">
          {campos.map((campo) => (
            <div
              key={campo}
              className={campo === 'notas' ? 'md:col-span-2' : ''}
            >
              <label
                htmlFor={`campo-${campo}`}
                className="mb-2 block text-sm font-medium text-slate-700"
              >
                {obtenerEtiqueta(campo)}
                {camposObligatorios.includes(campo) && (
                  <span className="ml-1 text-red-500">*</span>
                )}
              </label>

              {campo.toLowerCase().includes('fecha') ? (
                <input
                  id={`campo-${campo}`}
                  type="date"
                  name={campo}
                  value={formulario[campo] || ''}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                />
              ) : campo === 'notas' ? (
                <textarea
                  id={`campo-${campo}`}
                  name={campo}
                  value={formulario[campo] || ''}
                  onChange={handleChange}
                  rows={4}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                />
              ) : (
                <input
                  id={`campo-${campo}`}
                  name={campo}
                  value={formulario[campo] || ''}
                  onChange={handleChange}
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                />
              )}
            </div>
          ))}

          {mensajeError && (
            <div className="md:col-span-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {mensajeError}
            </div>
          )}

          {mensaje && (
            <div className="md:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
              {mensaje}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
          <button
            type="button"
            onClick={() => navigate('/instrumentos')}
            disabled={guardando}
            className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={guardando}
            className={`h-10 rounded-lg px-5 text-sm font-semibold text-white transition ${
              guardando
                ? 'cursor-not-allowed bg-slate-400'
                : 'bg-cyan-600 hover:bg-cyan-700'
            }`}
          >
            {guardando ? 'Guardando...' : 'Guardar instrumento'}
          </button>
        </div>
      </form>
    </div>
  );
}
